import React, { useEffect, useState } from "react";
import {
    fetchQuestions,
    adminFeatureQuestion,
    adminClearOverlay,
    adminAnswerQuestion,
    adminDeleteQuestion,
} from "../lib/api";
import { useQuestionsWS } from "../lib/useQuestionsWS";
import { Radio, Check, Trash2, ArrowBigUp, EyeOff, Crown as CrownIcon } from "lucide-react";
import { toast } from "sonner";

export const QAAdminPanel = ({ adminKey }) => {
    const { queue, featured, setQueue } = useQuestionsWS();
    const [busy, setBusy] = useState(null);

    // Cold-load fallback
    useEffect(() => {
        if (queue.length === 0) {
            fetchQuestions()
                .then(setQueue)
                .catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const feature = async (q) => {
        setBusy(q.id);
        try {
            await adminFeatureQuestion(q.id, adminKey);
            toast.success(`Featured: ${q.username}'s question is on stream`);
        } catch {
            toast.error("Failed to feature");
        } finally {
            setBusy(null);
        }
    };

    const clearOverlay = async () => {
        setBusy("clear");
        try {
            await adminClearOverlay(adminKey);
            toast.success("Overlay cleared");
        } catch {
            toast.error("Failed to clear overlay");
        } finally {
            setBusy(null);
        }
    };

    const markAnswered = async (q) => {
        setBusy(q.id);
        try {
            await adminAnswerQuestion(q.id, true, adminKey);
            toast.success("Marked answered");
        } catch {
            toast.error("Failed to update");
        } finally {
            setBusy(null);
        }
    };

    const remove = async (q) => {
        if (!confirm(`Delete "${q.text.slice(0, 60)}…"?`)) return;
        setBusy(q.id);
        try {
            await adminDeleteQuestion(q.id, adminKey);
            toast.success("Deleted");
        } catch {
            toast.error("Failed to delete");
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="space-y-5">
            {/* Featured banner */}
            <div
                className={`bg-surface border-4 border-black p-5 ${featured ? "brutal-shadow-pink" : "brutal-shadow"}`}
            >
                <div className="flex items-center gap-2 mb-2">
                    <Radio
                        className={`w-4 h-4 ${featured ? "text-hotpink animate-pulse" : "text-white/30"}`}
                        strokeWidth={3}
                    />
                    <span className="font-anton uppercase tracking-widest text-xs text-white">
                        On-Stream Overlay
                    </span>
                    {featured && (
                        <button
                            onClick={clearOverlay}
                            disabled={busy === "clear"}
                            className="ml-auto inline-flex items-center gap-1 border-2 border-hotpink text-hotpink font-anton uppercase text-xs px-3 py-1.5 hover:bg-hotpink hover:text-black transition-colors disabled:opacity-60"
                        >
                            <EyeOff className="w-3 h-3" /> Clear Overlay
                        </button>
                    )}
                </div>
                {featured ? (
                    <div className="bg-black border-2 border-hotpink p-4">
                        <div className="font-anton uppercase tracking-wider text-hotpink text-base">
                            {featured.username}
                        </div>
                        <p className="font-chivo text-white mt-1">{featured.text}</p>
                    </div>
                ) : (
                    <p className="font-chivo text-white/50 text-sm">
                        Nothing live right now. Hit "Feature on Stream" below to
                        broadcast a question to every viewer.
                    </p>
                )}
            </div>

            {/* Queue */}
            <div className="space-y-3">
                {queue.length === 0 ? (
                    <div className="bg-surface border-2 border-white/10 p-12 text-center font-chivo text-white/50">
                        No pending questions.
                    </div>
                ) : (
                    queue.map((q, i) => (
                        <article
                            key={q.id}
                            data-testid="admin-question"
                            className={`bg-surface border-4 border-black p-4 flex items-start gap-3 ${q.featured ? "brutal-shadow-pink" : "brutal-shadow"}`}
                        >
                            <div className="flex flex-col items-center justify-center px-3 py-2 border-2 border-white/15 min-w-[56px]">
                                <ArrowBigUp
                                    className="w-5 h-5 text-kickgreen fill-kickgreen"
                                    strokeWidth={2.5}
                                />
                                <span className="font-anton text-xl text-white leading-none">
                                    {q.votes}
                                </span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-anton uppercase text-xs text-white/40 border border-white/20 px-1.5 py-0.5">
                                        #{i + 1}
                                    </span>
                                    <span className="font-anton uppercase tracking-wider text-base text-white truncate">
                                        {q.username}
                                    </span>
                                    {q.featured && (
                                        <span className="inline-flex items-center gap-1 bg-hotpink text-white font-anton uppercase text-[10px] tracking-widest px-2 py-0.5 border-2 border-black">
                                            <CrownIcon className="w-3 h-3" /> Featured
                                        </span>
                                    )}
                                </div>
                                <p className="font-chivo text-sm text-white/85 mt-1 break-words">
                                    {q.text}
                                </p>
                            </div>
                            <div className="flex flex-col gap-2">
                                <button
                                    data-testid="qa-feature-btn"
                                    onClick={() => feature(q)}
                                    disabled={busy === q.id || q.featured}
                                    className="inline-flex items-center gap-1 bg-hotpink text-white font-anton uppercase text-xs px-3 py-2 border-2 border-black hover:-translate-y-0.5 hover:shadow-[4px_4px_0_#53FC18] transition-all disabled:opacity-50"
                                >
                                    <Radio className="w-3 h-3" strokeWidth={3} />
                                    {q.featured ? "Live" : "Feature"}
                                </button>
                                <button
                                    onClick={() => markAnswered(q)}
                                    disabled={busy === q.id}
                                    className="inline-flex items-center gap-1 bg-kickgreen text-black font-anton uppercase text-xs px-3 py-2 border-2 border-black hover:-translate-y-0.5 hover:shadow-[4px_4px_0_#0AC3FF] transition-all disabled:opacity-50"
                                >
                                    <Check className="w-3 h-3" strokeWidth={3} />
                                    Answered
                                </button>
                                <button
                                    onClick={() => remove(q)}
                                    disabled={busy === q.id}
                                    className="inline-flex items-center gap-1 border-2 border-hotpink text-hotpink font-anton uppercase text-xs px-3 py-2 hover:bg-hotpink hover:text-black transition-colors disabled:opacity-50"
                                >
                                    <Trash2 className="w-3 h-3" />
                                    Delete
                                </button>
                            </div>
                        </article>
                    ))
                )}
            </div>
        </div>
    );
};
